import { ContentDocumentError, createWorkspaceDocument, validateContentDocument } from './document.mjs';

const clone = value => structuredClone(value);
const equal = (left, right) => {
  if (left === right) return true;
  if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object'
    || Array.isArray(left) !== Array.isArray(right)) return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every(key => Object.hasOwn(right, key) && equal(left[key], right[key]));
};

function check(document) {
  const result = validateContentDocument(document);
  if (!result.valid) throw new ContentDocumentError(result.issues);
}

function checkDesigns(outputDesigns) {
  check({ ...createWorkspaceDocument(), outputDesigns });
}

function slot(element, property, address) {
  const keys = property.split('.');
  let holder = element;
  for (const key of keys.slice(0, -1)) {
    if (holder === null || typeof holder !== 'object' || !Object.hasOwn(holder, key)) throw new Error(`${address}: 디자인 속성이 없습니다.`);
    holder = holder[key];
  }
  const key = keys.at(-1);
  if (holder === null || typeof holder !== 'object' || !Object.hasOwn(holder, key)) throw new Error(`${address}: 디자인 속성이 없습니다.`);
  return { holder, key };
}

function currentBase(actual, base, address) {
  if (!equal(actual, base)) throw new Error(`${address}: 디자인이 바뀌었습니다. 최신 값을 확인해 주세요.`);
}

function replace(edits, matches, edit) {
  const result = [];
  let inserted = false;
  for (const previous of edits) {
    if (!matches(previous)) result.push(previous);
    else if (!inserted && edit) { result.push(edit); inserted = true; }
  }
  if (!inserted && edit) result.push(edit);
  return result;
}

/** Keep edits made during a build, expressed against the design that build has published. */
function rebase(edits, applied) {
  const groupOf = address => address.startsWith('layout/') ? `${address.split('/').slice(0, 3).join('/')}/` : address;
  let result = clone(edits);
  for (const group of new Set(applied.map(edit => groupOf(edit.address)))) {
    const belongs = edit => groupOf(edit.address) === group;
    const written = applied.filter(belongs), pending = result.filter(belongs);
    const publishedElement = written.find(edit => edit.address.endsWith('/element'));
    const pendingElement = pending.find(edit => edit.address.endsWith('/element'));
    let next;
    if (publishedElement || pendingElement) {
      const base = clone(publishedElement ? publishedElement.value : pendingElement.base);
      const value = clone(pendingElement ? pendingElement.value : publishedElement.base);
      // A scalar edit can become a deletion/replacement while the build is running, or a
      // structural edit can be undone and followed by new scalar edits. Preserve both cases.
      for (const edit of publishedElement ? pending : written) {
        if (edit.address.endsWith('/element')) continue;
        const { holder, key } = slot(publishedElement ? value : base, edit.address.slice(group.length), edit.address);
        holder[key] = clone(edit.value);
      }
      next = equal(base, value) ? [] : [{ address: `${group}element`, base, value }];
    } else {
      next = pending.map(edit => {
        const previous = written.find(item => item.address === edit.address);
        return previous ? { ...edit, base: clone(previous.value) } : edit;
      });
      for (const edit of written) {
        if (!pending.some(item => item.address === edit.address)) next.push({ address: edit.address, base: clone(edit.value), value: clone(edit.base) });
      }
      next = next.filter(edit => !equal(edit.base, edit.value));
    }
    const index = result.findIndex(belongs);
    result = result.filter(edit => !belongs(edit));
    result.splice(index < 0 ? result.length : index, 0, ...next);
  }
  return result;
}

/** Output adapters share this editor; its pending design belongs to the current Studio document. */
export function documentDesignEdits({ document, changed, outputId }) {
  if (typeof outputId !== 'string') throw new TypeError('출력 ID는 문자열이어야 합니다.');
  checkDesigns({ [outputId]: { edits: [] } });
  const read = (content = document()) => {
    const designs = Object.hasOwn(content, 'outputDesigns') ? content.outputDesigns : {};
    checkDesigns(designs);
    return { content, designs, edits: designs[outputId]?.edits ?? [] };
  };
  const commit = (state, edits, address) => {
    if (!equal(state.edits, edits)) {
      const outputDesigns = { ...state.designs, [outputId]: { edits: clone(edits) } };
      check({ ...state.content, outputDesigns });
      state.content.outputDesigns = outputDesigns;
      if (document() === state.content) changed(address);
    }
    return clone(edits);
  };
  return Object.freeze({
    note: '디자인과 그림 선택은 이 문서에 저장됩니다. 템플릿으로 저장하면 복사한 문서에도 함께 적용됩니다.',
    target: '문서 디자인',
    available: async () => true,
    list: async () => clone(read().edits),
    rebaseApplied(appliedDocument, targetDocument) {
      const applied = read(appliedDocument).edits;
      const state = read(targetDocument);
      return commit(state, rebase(state.edits, applied), `outputDesigns/${outputId}`);
    },
    async save(edit) {
      checkDesigns({ [outputId]: { edits: [edit] } });
      const { address, base, value } = clone(edit);
      const state = read(), previous = state.edits.find(item => item.address === address);
      const parts = address.split('/');
      if (parts[0] === 'layout') {
        const prefix = `${parts.slice(0, 3).join('/')}/`, structuralAddress = `${prefix}element`;
        const structural = state.edits.find(item => item.address === structuralAddress);
        const related = item => item.address.startsWith(prefix);
        if (parts[3] === 'element') {
          let original;
          if (structural) {
            currentBase(structural.value, base, address);
            original = structural.base;
          } else {
            original = clone(base);
            // The canvas passes the displayed element. Undo its pending properties to retain
            // the original definition required when this structural edit is rendered later.
            for (const scalar of state.edits.filter(related).reverse()) {
              const { holder, key } = slot(original, scalar.address.slice(prefix.length), scalar.address);
              currentBase(holder[key], scalar.value, scalar.address);
              holder[key] = clone(scalar.base);
            }
          }
          if (equal(base, value)) return clone(state.edits);
          return commit(state, replace(state.edits, related, equal(original, value) ? null
            : { address, base: original, value }), address);
        }
        if (structural) {
          const definition = clone(structural.value);
          const { holder, key } = slot(definition, parts[3], address);
          currentBase(holder[key], base, address);
          holder[key] = value;
          return commit(state, replace(state.edits, related, equal(structural.base, definition) ? null
            : { address: structuralAddress, base: structural.base, value: definition }), address);
        }
      }
      if (previous) currentBase(previous.value, base, address);
      const original = previous ? previous.base : base;
      return commit(state, replace(state.edits, item => item.address === address, equal(original, value) ? null
        : { address, base: original, value }), address);
    },
    async revert(address) {
      checkDesigns({ [outputId]: { edits: [{ address, base: null, value: null }] } });
      const state = read();
      const prefix = address.startsWith('layout/') && address.endsWith('/element') ? address.slice(0, -'element'.length) : null;
      return commit(state, state.edits.filter(item => prefix ? !item.address.startsWith(prefix) : item.address !== address), address);
    },
  });
}
